import { computed, ref, watchEffect } from 'vue'

import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'

import { hasAnnouncementStarted } from '@/apiClient/modules/announcementMapping'
import { AnnouncementsApi } from '@/apiClient/modules/announcements'
import { announcementQueryKeys } from '@/apiClient/queries/useAnnouncementQueries'
import type { IActiveAnnouncement } from '@/apiClient/types/announcements'
import createLogger from '@/lib/logger'
import {
  isPosthogFlagOn,
  PosthogFeatureFlags,
  usePosthogFlag,
  usePosthogFlagsSettled,
} from '@/lib/posthog'
import { useAnnouncementModalsStore } from '@/store/announcementModals'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { getCookie, setCookie } from '@/utils/cookies'
import {
  isSessionDismissed,
  setSessionDismissed,
} from '@/utils/sessionDismissal'

import {
  getAnnouncementModalConfigs,
  type AnnouncementModalConfig,
} from './announcementModals.config'
import {
  announcementToView,
  configToView,
  type AnnouncementModalView,
} from './announcementModalView'

const logger = createLogger('useAnnouncementModals')

const resolveDismissalDays = (
  config: AnnouncementModalConfig,
  reminderChecked: boolean,
): number | undefined => {
  if (!config.reminder) return undefined
  return reminderChecked
    ? config.reminder.daysWhenChecked
    : config.reminder.daysWhenUnchecked
}

export function useAnnouncementModals() {
  const modals = useAnnouncementModalsStore()
  const { current } = storeToRefs(modals)
  const { isUserAuthorisedForEverGpt } = storeToRefs(useAppStore())
  const { user } = storeToRefs(useAuthStore())

  const modalConfigs = getAnnouncementModalConfigs()

  /**
   * Global kill switch for announcements. Off means no active-list fetch and no
   * modals (API-backed or built-in fallback). The Admin tab is gated separately
   * in `Admin/Index.vue` with the same flag.
   *
   * The fetch waits until PostHog has actually reported flags. `usePosthogFlag`
   * is fail-open before that, and starting the query on that default is what
   * called `GET /announcements` on the home page while the flag was off.
   */
  const isAnnouncementsEnabled = usePosthogFlag(
    PosthogFeatureFlags.Announcements,
  )
  const haveFeatureFlagsSettled = usePosthogFlagsSettled()

  /**
   * Bumped on every dismissal. Eligibility depends on cookies and
   * `sessionStorage`, neither of which is reactive, so without this the `views`
   * computed would keep its cached result and the queue would immediately
   * re-enqueue the modal the user just closed.
   */
  const dismissalVersion = ref(0)

  const isActiveQueryEnabled = computed(
    () =>
      haveFeatureFlagsSettled.value &&
      isUserAuthorisedForEverGpt.value &&
      !!user.value &&
      isAnnouncementsEnabled.value,
  )

  const { data: activeAnnouncements, isError: hasAnnouncementsFailed } =
    useQuery<IActiveAnnouncement[]>({
      // Reactive so a user switch re-keys the query instead of reusing the
      // previous account's cached list.
      queryKey: computed(() => announcementQueryKeys.active(user.value?.id)),
      queryFn: () => AnnouncementsApi.getActive(),
      // Gated on a user too: sign-out clears it, and a request fired then goes
      // through `refreshKeycloakToken`, which writes the tokens straight back.
      enabled: isActiveQueryEnabled,
      // An API without the endpoint answers 404 every time; retrying it on a
      // schedule only delays the fallback the failure is meant to trigger.
      retry: false,
      staleTime: 5 * 60 * 1000,
    })

  /**
   * The built-in configs are a *fallback for an API that cannot answer*, not for
   * one that answers with nothing: an empty list means the admins have published
   * nothing and the hardcoded notices must stay quiet. Only a failed request —
   * a 404 from an API predating the endpoint included — puts them on screen.
   */
  const isUsingFallbackConfigs = computed(
    () => isAnnouncementsEnabled.value && hasAnnouncementsFailed.value,
  )

  const isConfigEligible = (config: AnnouncementModalConfig): boolean => {
    if (!isPosthogFlagOn(config.posthogFeatureFlag)) return false
    // Presence of the dismissal cookie suppresses the modal until it expires.
    if (getCookie(config.dismissCookie)) return false
    return config.isEligible?.() ?? true
  }

  const views = computed<AnnouncementModalView[]>(() => {
    // Read the counter so dismissals invalidate this computed.
    void dismissalVersion.value

    if (!haveFeatureFlagsSettled.value || !isAnnouncementsEnabled.value) {
      return []
    }

    const userId = user.value?.id
    const candidates = isUsingFallbackConfigs.value
      ? modalConfigs.filter(isConfigEligible).map(configToView)
      : (activeAnnouncements.value ?? [])
          // The server cannot see `scheduled_start_at` — it lives in the opaque
          // `data` blob — so holding a scheduled notice back is the client's
          // job. Read against the clock rather than a reactive source: a window
          // that opens mid-session starts at the next load, which is soon
          // enough for something scheduled days out.
          .filter((announcement) => hasAnnouncementStarted(announcement))
          .map(announcementToView)

    // Applied to both sources: it is what stops a just-acknowledged modal being
    // re-enqueued before the recorded dismissal is readable — the active list is
    // still the pre-dismissal one until it refetches, and config eligibility is
    // cookie-based, so neither source reflects the close immediately.
    return candidates.filter((view) => !isSessionDismissed(view.id, userId))
  })

  const viewsById = computed(
    () => new Map(views.value.map((view) => [view.id, view])),
  )

  // Keep the queue in sync with eligibility: drop the current modal if it becomes
  // ineligible, and enqueue any eligible modal that isn't already pending.
  watchEffect(() => {
    try {
      const currentItem = current.value
      if (currentItem && !viewsById.value.has(currentItem.configId)) {
        modals.dismissCurrent()
      }

      for (const view of views.value) {
        if (modals.isPending(view.id)) continue
        modals.enqueue(view.id)
      }
    } catch (error) {
      logger.error('Error enqueueing announcement modals', error)
    }
  })

  const active = computed<AnnouncementModalView | null>(() => {
    const currentItem = current.value
    if (!currentItem) return null
    return viewsById.value.get(currentItem.configId) ?? null
  })

  const dismissAnnouncement = (id: string, reminderChecked: boolean) => {
    if (!isAnnouncementsEnabled.value) return
    // Fire-and-forget: a failed dismissal should not block the modal from
    // closing, and the session guard already hides it for this tab.
    void AnnouncementsApi.dismiss(id, reminderChecked).catch((error) =>
      logger.error('Could not record the dismissal', error),
    )
  }

  const dismissConfig = (configId: string, reminderChecked: boolean) => {
    const config = modalConfigs.find((item) => item.id === configId)
    if (!config) return

    setCookie(
      config.dismissCookie,
      'true',
      resolveDismissalDays(config, reminderChecked),
    )
  }

  /** Suppresses the current modal for this tab session and advances the queue. */
  const advance = (viewId: string) => {
    setSessionDismissed(viewId, user.value?.id)
    dismissalVersion.value += 1
    // Advance here rather than leaving it to the watcher: the effect is
    // scheduled, so a second dismissal from it would skip the next modal.
    modals.dismissCurrent()
  }

  /**
   * The only way a announcement closes. The dialog is acknowledge-only — it
   * carries a header image, so ui-lib gives it no close control and neither
   * Escape nor a backdrop click dismisses it — which means every close is an
   * acknowledgement and is always recorded, with `reminderChecked` deciding
   * whether that is permanent.
   */
  const handleClose = (reminderChecked: boolean) => {
    const currentItem = current.value
    if (!currentItem) return

    const [source, ...rest] = currentItem.configId.split(':')
    const sourceId = rest.join(':')

    if (source === 'announcement')
      dismissAnnouncement(sourceId, reminderChecked)
    else dismissConfig(sourceId, reminderChecked)

    advance(currentItem.configId)
  }

  return {
    active,
    handleClose,
  }
}
